import { useState, useEffect, useCallback } from 'react';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';

interface EmployeeTestModeState {
  enabled: boolean;
  loading: boolean;
  refetch: () => void;
}

// Custom event name for broadcasting mode changes across hook instances
const MODE_CHANGE_EVENT = 'employee-test-mode-changed';

/**
 * Checks if employee_only_mode is enabled in demo_settings.
 * All instances stay in sync via a custom DOM event.
 */
export function useEmployeeTestMode(): EmployeeTestModeState {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    const { data } = await supabase
      .from('demo_settings' as any)
      .select('employee_only_mode')
      .limit(1)
      .maybeSingle();
    const val = (data as any)?.employee_only_mode === true;
    setEnabled(val);
    setLoading(false);
    return val;
  }, []);

  useEffect(() => {
    check();
    const interval = setInterval(check, 30000);
    
    // Listen for broadcasts from other instances (e.g. banner toggle)
    const handler = () => { check(); };
    window.addEventListener(MODE_CHANGE_EVENT, handler);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener(MODE_CHANGE_EVENT, handler);
    };
  }, [check]);

  const refetch = useCallback(() => {
    check().then(() => {
      // Broadcast to all other hook instances
      window.dispatchEvent(new Event(MODE_CHANGE_EVENT));
    });
  }, [check]);

  return { enabled, loading, refetch };
}
